/**
 * Lebanese phone numbers, as a person reads and types them.
 *
 * These mirror the server rules in `./phone.ts` and `./authForms.ts`,
 * deliberately reusing the server's exact error copy so one mistake never
 * produces two different messages. The server stays the authority and
 * re-validates everything; these exist so a field can be checked before a step
 * advances, and so a number is always grouped, whether it is being typed or
 * being read back.
 *
 * **A phone number is never shown as an unbroken run of digits.** Nobody reads
 * `+96171234456`; everybody reads `+961 71 234 456`. Every place any Bookr
 * client renders or accepts a number goes through here.
 *
 * It was `src/app/_components/lebanesePhone.ts` until R22 T11. It moved into
 * Identity, behind the browser-safe `surfaces.ts` entry, when the static web
 * app needed it too: a phone formatter that exists once on the server and once
 * per client is three chances to group a number differently, and the number in
 * question is how somebody gets told their appointment is confirmed.
 */

/** Shape check. Returns the message to show, or undefined when it is fine. */
export function checkPhone(value: string): string | undefined {
  if (!value.trim()) return "Phone number is required";
  if (!/^\d{7,8}$/.test(nationalDigits(value))) {
    return "Enter a valid Lebanese phone number";
  }
  return undefined;
}

/**
 * The national significant number, from any form a person or a database might
 * hand us: `+961 71 234 456`, `0096171234456`, `96171234456`, `071234456`, or
 * a bare `71234456`.
 *
 * Stripping the country code is the part that used to be missing, and it was
 * not cosmetic: a stored E.164 number rendered as `96 171 234`, which is not
 * the customer's number at all. Anything that reaches a screen must come
 * through here first.
 */
export function nationalDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const withoutCountry = digits.startsWith("00961")
    ? digits.slice(5)
    : digits.startsWith("961")
      ? digits.slice(3)
      : digits;
  // The trunk prefix people write locally, never part of the number itself.
  return withoutCountry.startsWith("0") ? withoutCountry.slice(1) : withoutCountry;
}

/**
 * Groups the national number, for display and as you type: `71 234 456`.
 *
 * Lebanese numbers carry a 7- or 8-digit national significant number, so the
 * leading group is one digit for 7 (`3 123 456`) and two for 8 (`71 234 456`).
 * Grouping is cosmetic: the server's normalizer ignores the spaces.
 */
export function formatLebanesePhone(raw: string): string {
  const digits = nationalDigits(raw).slice(0, 8);
  const head = digits.length > 7 ? 2 : 1;
  if (digits.length <= head) return digits;
  return [digits.slice(0, head), digits.slice(head, head + 3), digits.slice(head + 3)]
    .filter(Boolean)
    .join(" ");
}

/**
 * The same number with its dialling code, for anywhere the country is not
 * already implied by a `+961` affix on the field: `+961 71 234 456`.
 *
 * An unparseable value is returned untouched rather than mangled into a
 * plausible-looking wrong number, because a phone number that is quietly wrong
 * is worse than one that is visibly odd.
 */
export function formatLebanesePhoneFull(raw: string): string {
  const grouped = formatLebanesePhone(raw);
  if (grouped.replace(/\s/g, "").length < 7) return raw.trim();
  return `+961 ${grouped}`;
}
